import type { Direction, Position } from '@/types/game';
import { buildTeleporterMap, positionKey } from '@/game/teleporters';

export interface TraversalLevel {
  grid: string[];
  start: Position;
  end: Position;
  teleporterMap: Map<string, Position>;
}

export interface SlideResult {
  start: Position;
  direction: Direction;
  stop: Position;
  end: Position;
  traversed: Position[];
  teleportedFrom: Position | null;
}

export interface TraversalRoute {
  directions: Direction[];
  stops: Position[];
  traversed: Position[];
}

export interface CollectibleSolveResult {
  solvable: boolean;
  directions: Direction[];
  stops: Position[];
  traversed: Position[];
}

const DIRECTIONS: Direction[] = ['up', 'down', 'left', 'right'];

const DIRECTION_VECTORS: Record<Direction, [number, number]> = {
  up: [0, -1],
  down: [0, 1],
  left: [-1, 0],
  right: [1, 0],
};

export function parseTraversalLevel(grid: string[]): TraversalLevel {
  let start: Position = [0, 0];
  let end: Position = [0, 0];

  grid.forEach((row, y) => {
    row.split('').forEach((cell, x) => {
      const lower = cell.toLowerCase();
      if (lower === 'o') start = [x, y];
      if (lower === 'e') end = [x, y];
    });
  });

  return {
    grid,
    start,
    end,
    teleporterMap: buildTeleporterMap(grid),
  };
}

function isInBounds(grid: string[], pos: Position): boolean {
  const [x, y] = pos;
  if (y < 0 || y >= grid.length) return false;
  const rowWidth = grid[y]?.length ?? 0;
  return x >= 0 && x < rowWidth;
}

function getCell(grid: string[], pos: Position): string {
  if (!isInBounds(grid, pos)) return '';
  return grid[pos[1]][pos[0]].toLowerCase();
}

export function isCollectibleTile(grid: string[], pos: Position): boolean {
  const cell = getCell(grid, pos);
  return cell !== '' && !['x', 'l', 's', 't', 'n', 'o', 'e'].includes(cell);
}

export function slideInDirection(level: TraversalLevel, start: Position, direction: Direction): SlideResult {
  const [dx, dy] = DIRECTION_VECTORS[direction];
  const traversed: Position[] = [];
  let current: Position = [start[0], start[1]];

  while (true) {
    const next: Position = [current[0] + dx, current[1] + dy];
    if (!isInBounds(level.grid, next)) break;

    const nextCell = getCell(level.grid, next);
    if (nextCell === 'x' || nextCell === 'l') break;

    current = [next[0], next[1]];
    traversed.push([current[0], current[1]]);

    if (nextCell === 's' || nextCell === 't' || nextCell === 'n') {
      break;
    }
  }

  const stop: Position = [current[0], current[1]];
  let end: Position = [stop[0], stop[1]];
  let teleportedFrom: Position | null = null;

  if (traversed.length > 0) {
    const destination = level.teleporterMap.get(positionKey(stop));
    if (destination) {
      teleportedFrom = [stop[0], stop[1]];
      end = [destination[0], destination[1]];
      traversed.push([end[0], end[1]]);
    }
  }

  return {
    start: [start[0], start[1]],
    direction,
    stop,
    end,
    traversed,
    teleportedFrom,
  };
}

function positionEquals(a: Position, b: Position): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

function parseStateKey(stateKey: string): { pos: Position; mask: number } {
  const [posPart, maskPart] = stateKey.split('|');
  const [x, y] = posPart.split(',').map(Number);
  return {
    pos: [x, y],
    mask: Number(maskPart),
  };
}

export function findTraversalRouteToExit(grid: string[]): TraversalRoute | null {
  const level = parseTraversalLevel(grid);
  const startKey = positionKey(level.start);
  const queue: Position[] = [[level.start[0], level.start[1]]];
  const visited = new Set<string>([startKey]);
  const parents = new Map<
    string,
    {
      prevKey: string;
      direction: Direction;
      traversed: Position[];
    }
  >();

  if (positionEquals(level.start, level.end)) {
    return {
      directions: [],
      stops: [[level.start[0], level.start[1]]],
      traversed: [],
    };
  }

  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentKey = positionKey(current);

    for (const direction of DIRECTIONS) {
      const move = slideInDirection(level, current, direction);
      if (positionEquals(move.end, current)) continue;

      const nextKey = positionKey(move.end);
      if (visited.has(nextKey)) continue;
      visited.add(nextKey);
      parents.set(nextKey, {
        prevKey: currentKey,
        direction,
        traversed: move.traversed,
      });

      if (positionEquals(move.end, level.end)) {
        const directions: Direction[] = [];
        const stops: Position[] = [[level.end[0], level.end[1]]];
        const traversedSegments: Position[][] = [];
        let cursor = nextKey;

        while (cursor !== startKey) {
          const parent = parents.get(cursor);
          if (!parent) break;
          directions.push(parent.direction);
          traversedSegments.push(parent.traversed);

          const [x, y] = parent.prevKey.split(',').map(Number);
          stops.push([x, y]);
          cursor = parent.prevKey;
        }

        directions.reverse();
        stops.reverse();
        const traversed = traversedSegments.reverse().flat();
        return {
          directions,
          stops,
          traversed,
        };
      }

      queue.push(move.end);
    }
  }

  return null;
}

export function solveLevelWithCollectibles(
  grid: string[],
  collectiblePositions: Position[],
): CollectibleSolveResult {
  const level = parseTraversalLevel(grid);
  const uniqueCollectibles = Array.from(
    new Map(collectiblePositions.map((pos) => [positionKey(pos), pos])).values(),
  );
  const collectibleIndex = new Map<string, number>();
  uniqueCollectibles.forEach((pos, index) => {
    collectibleIndex.set(positionKey(pos), index);
  });

  const targetMask = uniqueCollectibles.length > 0 ? (1 << uniqueCollectibles.length) - 1 : 0;

  let startMask = 0;
  const startCollectibleIndex = collectibleIndex.get(positionKey(level.start));
  if (startCollectibleIndex !== undefined) {
    startMask |= 1 << startCollectibleIndex;
  }

  const startStateKey = `${positionKey(level.start)}|${startMask}`;
  const queue: Array<{ pos: Position; mask: number; stateKey: string }> = [
    { pos: [level.start[0], level.start[1]], mask: startMask, stateKey: startStateKey },
  ];
  const visited = new Set<string>([startStateKey]);
  const parents = new Map<
    string,
    {
      prevKey: string;
      direction: Direction;
      traversed: Position[];
    }
  >();

  if (positionEquals(level.start, level.end) && startMask === targetMask) {
    return {
      solvable: true,
      directions: [],
      stops: [[level.start[0], level.start[1]]],
      traversed: [],
    };
  }

  while (queue.length > 0) {
    const current = queue.shift()!;

    for (const direction of DIRECTIONS) {
      const move = slideInDirection(level, current.pos, direction);
      if (positionEquals(move.end, current.pos)) continue;

      let nextMask = current.mask;
      for (const tile of move.traversed) {
        const index = collectibleIndex.get(positionKey(tile));
        if (index !== undefined) {
          nextMask |= 1 << index;
        }
      }

      const nextStateKey = `${positionKey(move.end)}|${nextMask}`;
      if (visited.has(nextStateKey)) continue;
      visited.add(nextStateKey);
      parents.set(nextStateKey, {
        prevKey: current.stateKey,
        direction,
        traversed: move.traversed,
      });

      if (positionEquals(move.end, level.end) && nextMask === targetMask) {
        const directions: Direction[] = [];
        const stops: Position[] = [[level.end[0], level.end[1]]];
        const traversedSegments: Position[][] = [];
        let cursor = nextStateKey;

        while (cursor !== startStateKey) {
          const parent = parents.get(cursor);
          if (!parent) break;
          directions.push(parent.direction);
          traversedSegments.push(parent.traversed);

          const { pos } = parseStateKey(parent.prevKey);
          stops.push([pos[0], pos[1]]);
          cursor = parent.prevKey;
        }

        directions.reverse();
        stops.reverse();

        return {
          solvable: true,
          directions,
          stops,
          traversed: traversedSegments.reverse().flat(),
        };
      }

      queue.push({
        pos: move.end,
        mask: nextMask,
        stateKey: nextStateKey,
      });
    }
  }

  return {
    solvable: false,
    directions: [],
    stops: [],
    traversed: [],
  };
}
